import React, { useCallback, useEffect, useState } from "react";

// region Imports - External libraries
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, ListItemText
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
// endregion Imports - External libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, LocationsModuleMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { AlertTypes } from "@shared/constants/alert-types.enum";
import { Alert } from "@shared/interfaces/alert.interface";
import { Travel } from "@shared/interfaces/travel.interface";
import { VehicleState } from "@shared/interfaces/vehicle-state.interface";
import { WaterAddition } from "@shared/interfaces/water-addition.interface";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Components
import { ModalVehicleAndTravelDetails } from "@components/Modal/VehicleAndTravelDetails";
// endregion Imports - Components
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

// region Constants
// Travels are lazily loaded in pages of this size (offset/limit pagination)
const PAGE_SIZE = 20;
// endregion Constants

type ModalConstructionTravelsProps = {
  open: boolean;
  onClose: () => void;
  idLocation: string;
  locationName?: string;
};

/**
 * Lists the finished travels whose destination is a given construction (obra).
 * The list is lazily paginated (offset/limit); each row opens the existing travel-details modal.
 */
const ModalConstructionTravels: React.FC<ModalConstructionTravelsProps> = ({ open, onClose, idLocation, locationName }) => {

  // region Hooks
  const { addToast } = useToast();
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [travels, setTravels] = useState<Travel[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Selected travel details (nested travel-details modal)
  const [selectedTravel, setSelectedTravel] = useState<Travel>({} as Travel);
  const [travelStates, setTravelStates] = useState<VehicleState[]>([]);
  const [travelAlerts, setTravelAlerts] = useState<Alert[]>([]);
  const [travelWaterAdditions, setTravelWaterAdditions] = useState<WaterAddition[]>([]);
  const [openTravelDetails, setOpenTravelDetails] = useState(false);
  const [loadingTravelDetails, setLoadingTravelDetails] = useState(false);
  // endregion States
  // region Functions

  /** Fetch a page of travels heading to this construction and append it to the list
   * @param offset Records to skip (0 for the first page)
   */
  const fetchTravels = useCallback(async (offset: number) => {

    setLoading(true);

    try {

      const { data } = await api.post("travels/get-filtered-by", {
        idDestinationLocations: [idLocation],
        finished: true,
        offset,
        limit: PAGE_SIZE
      });

      if (data.status === "success") {

        const result = (data.result ?? []) as Travel[];

        setTravels((current) => (offset === 0 ? result : [...current, ...result]));
        setHasMore(result.length === PAGE_SIZE);

      } else {
        // No (more) travels — e.g. the "Nenhuma viagem" alert when the last full page was an exact
        // multiple of the page size. Stop paginating so the "load more" button does not repeat forever.
        setHasMore(false);
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoading(false);
    }

  }, [addToast, idLocation, t]);

  /** Read the details of a travel (states, alerts, water additions) and open the travel-details modal.
   * Mirrors the flow used by the travels history report.
   * @param travel Target travel
   */
  const readTravelDetails = useCallback(async (travel: Travel) => {

    setSelectedTravel(travel);
    setTravelStates([]);
    setTravelAlerts([]);
    setTravelWaterAdditions([]);

    // Internal functions
    const FreadTravelStates = async () => (await api.get(`vehicles/states/get-filtered-by-travel/${travel.id_travel}`)).data;
    const FreadTravelAlerts = async (params: { idTravel: string, justified?: boolean }) => (await api.post("alerts/read", params)).data;
    const FreadWaterAdditions = async () => (await api.get(`water-additions/get-filtered-by-travel/${travel.id_travel}`)).data;

    setLoadingTravelDetails(true);

    try {

      let alertMessageContent = "";

      const [travelStatesResponse, travelAlertsResponse, travelWaterAdditionsResponse] = await Promise.all([
        FreadTravelStates(),
        // Justified alerts too: the details modal shows them apart from the pending ones
        FreadTravelAlerts({ idTravel: travel.id_travel }),
        FreadWaterAdditions()
      ]);

      // Verify if have some alert in states travel response
      if (travelStatesResponse.status === "success") setTravelStates(travelStatesResponse.result);
      else alertMessageContent += travelStatesResponse.message;

      // Verify if have some alert in alerts travel response
      if (travelAlertsResponse.status === "success") {
        setTravelAlerts(travelAlertsResponse.result.filter((alert: Alert) => [
          AlertTypes.EXCESSO_DE_VELOCIDADE,
          AlertTypes.DESCARREGAMENTO_INDEVIDO,
          AlertTypes.PARADA_INDEVIDA,
          AlertTypes.ZMRC
        ].includes(alert.type.description as AlertTypes)));
      } else alertMessageContent += travelAlertsResponse.message;

      // Verify if have some alert in water additions travel response
      if (travelWaterAdditionsResponse.status === "success") setTravelWaterAdditions(travelWaterAdditionsResponse.result);
      else if (travelWaterAdditionsResponse.status === "error") alertMessageContent += travelWaterAdditionsResponse.message;

      // If have some alert, show message to user
      if (alertMessageContent) addToast({ type: "info", title: t(ToastMessages.alert), description: alertMessageContent });

      setOpenTravelDetails(true);

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingTravelDetails(false);
    }

  }, [addToast, t]);
  // endregion Functions
  // region Effects

  // (Re)load the first page whenever the modal opens for a construction
  useEffect(() => {

    if (open && idLocation) {
      setTravels([]);
      setHasMore(false);
      fetchTravels(0);
    }

  }, [open, idLocation, fetchTravels]);
  // endregion Effects

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth style={{ zIndex: 2001 }}>
      <DialogTitle>{t(LocationsModuleMessages.constructionTravelsTitle)}</DialogTitle>
      <DialogContent dividers>
        <Container>
          {locationName && <div className="locationName">{locationName}</div>}

          {travels.length > 0 && (
            <List disablePadding className="travelsList">
              {travels.map((travel, index) => (
                <React.Fragment key={travel.id_travel}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    button
                    disableRipple
                    className="travelRow"
                    disabled={loadingTravelDetails}
                    onClick={() => { if (!loadingTravelDetails) readTravelDetails(travel); }}
                  >
                    <ListItemText
                      primary={`${travel.vehicle?.code || "-"} · NF ${travel.num_doc || "-"}`}
                      secondary={utils.formatDateIfHave(travel.finish_date, "fullDateWithoutSeconds") as string}
                    />
                    <ChevronRightIcon />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}

          {loading && (
            <div className="loading"><CircularProgress size={24} /></div>
          )}

          {!loading && travels.length === 0 && (
            <div className="empty">{t(LocationsModuleMessages.constructionTravelsEmpty)}</div>
          )}

          {hasMore && !loading && (
            <div className="loadMore">
              <Button disableRipple color="primary" onClick={() => fetchTravels(travels.length)}>
                {t(GlobalMessages.showMore)}
              </Button>
            </div>
          )}
        </Container>
      </DialogContent>
      <DialogActions>
        <Button disableRipple onClick={onClose} color="primary">
          {t(GlobalMessages.close)}
        </Button>
      </DialogActions>

      {openTravelDetails && (
        <ModalVehicleAndTravelDetails
          vehicle={{ ...selectedTravel.vehicle, states: travelStates, alerts: travelAlerts }}
          travel={{ ...selectedTravel, water_additions: travelWaterAdditions }}
          open={openTravelDetails}
          onClose={() => setOpenTravelDetails(false)}
          historical
          overAll
        />
      )}
    </Dialog>
  );
};

export default ModalConstructionTravels;
